import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Phone, Clock, MapPin, Fuel, Droplets, Store, Wind, Building2, Coffee, Bed, User, Wrench, Wifi, Bath } from 'lucide-react';
import { stations } from '../data/mock';
import StationGallery from '../components/StationGallery';
import { useTranslation } from 'react-i18next';

const Stations = () => {
  const { t } = useTranslation();
  
  // Function to translate service names
  const translateService = (service) => {
    const serviceMap = {
      'Ofis': t('stations.serviceNames.ofis'),
      'Market': t('stations.serviceNames.market'),
      'Mağaza': t('stations.serviceNames.market'),
      'Restoran': t('stations.serviceNames.restoran'),
      'Kafe': t('stations.serviceNames.kafe'),
      'Motel': t('stations.serviceNames.motel'),
      'Namaz otağı': t('stations.serviceNames.namazOtagi'),
      'Avtoyuma': t('stations.serviceNames.avtoyuma'),
      'Avtomoyka': t('stations.serviceNames.avtoyuma'),
      'Texniki qulluq': t('stations.serviceNames.texnikiQulluq'),
      'Qaz (LPG)': t('stations.serviceNames.qaz'),
      'Azercell': t('stations.serviceNames.azercell'),
      'Hamam (Banyo)': t('stations.serviceNames.hamam'),
      'Hava kompressoru': t('stations.serviceNames.havaKompressoru')
    };
    return serviceMap[service] || service;
  };
  
  const getServiceIcon = (service) => {
    if (service === 'Ofis') {
      return <Building2 className="w-4 h-4" />;
    }
    if (service === 'Market' || service === 'Mağaza') {
      return <Store className="w-4 h-4" />;
    }
    if (service === 'Restoran' || service === 'Kafe') {
      return <Coffee className="w-4 h-4" />;
    }
    if (service === 'Motel') {
      return <Bed className="w-4 h-4" />;
    }
    if (service === 'Namaz otağı') {
      return <User className="w-4 h-4" />;
    }
    if (service === 'Avtoyuma' || service === 'Avtomoyka') {
      return <Droplets className="w-4 h-4" />;
    }
    if (service === 'Texniki qulluq') {
      return <Wrench className="w-4 h-4" />;
    }
    if (service === 'Qaz (LPG)') {
      return <Fuel className="w-4 h-4" />;
    }
    if (service === 'Azercell') {
      return <Wifi className="w-4 h-4" />;
    }
    if (service === 'Hamam (Banyo)') {
      return <Bath className="w-4 h-4" />;
    }
    if (service === 'Hava kompressoru') {
      return <Wind className="w-4 h-4" />;
    }
    return <Store className="w-4 h-4" />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-red-600 to-red-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">{t('stations.title')}</h1>
            <p className="text-xl text-red-100 leading-relaxed">
              {t('stations.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Stations Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {stations.map((station) => (
              <Card key={station.id} className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                {/* Image Gallery Slider */}
                <StationGallery images={station.images} stationName={station.name} />

                <CardContent className="p-6">
                  {/* Station Name */}
                  <h3 className="text-xl font-bold mb-4 text-gray-900">{station.name}</h3>

                  {/* Address */}
                  <div className="flex items-start gap-3 mb-3">
                    <MapPin className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-sm leading-relaxed">{station.address}</p>
                  </div>

                  {/* Working Hours */}
                  <div className="flex items-center gap-3 mb-3">
                    <Clock className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <p className="text-gray-700 text-sm">
                      <span className="font-semibold text-red-600">{t('stations.open247')}</span>
                    </p>
                  </div>

                  {/* Phone */}
                  <div className="flex items-center gap-3 mb-4">
                    <Phone className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <a href={`tel:${station.phone}`} className="text-gray-700 hover:text-red-600 transition-colors text-sm font-medium">
                      {station.phone}
                    </a>
                  </div>

                  {/* Services */}
                  <div className="border-t pt-4">
                    <p className="text-sm font-semibold text-gray-700 mb-3">{t('stations.services')}:</p>
                    <div className="flex flex-wrap gap-2">
                      {station.services.map((service, idx) => (
                        <Badge
                          key={idx}
                          variant="secondary"
                          className="bg-red-50 text-red-700 hover:bg-red-100 flex items-center gap-1.5 px-3 py-1"
                        >
                          {getServiceIcon(service)}
                          <span className="text-xs">{translateService(service)}</span>
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Car Wash Badge */}
                  {station.hasCarWash && (
                    <div className="mt-4 pt-4 border-t">
                      <Badge className="bg-green-100 text-green-700 hover:bg-green-200 flex items-center gap-2 w-fit">
                        <Droplets className="w-4 h-4" />
                        {t('stations.carWash')} {t('stations.available')}
                      </Badge>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-center">{t('stations.whyTitle')}</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Fuel className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">{t('stations.whyQuality')}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t('stations.whyQualityDesc')}
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">{t('stations.why24')}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t('stations.why24Desc')}
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Droplets className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">{t('stations.whyCarwash')}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t('stations.whyCarwashDesc')}
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">{t('stations.wideNetwork')}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t('stations.wideNetworkDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Stations;
